import { createContext, useContext } from "react";
import type { FeatureFlags, Locale, Me, ProjectRole, User } from "../api/types";
import { codesIn, isCapability, isMenuCode } from "./permissions";

/**
 * The signed-in principal, flags and modes (GET /auth/me + GET /features). Screens use this to render
 * deliberate states; the server still enforces every permission and visibility rule.
 */
export interface Session {
  me: Me;
  user: User;
  flags: Record<string, boolean>;
  modes: Me["modes"];
  locale: Locale;
  isPartner: boolean;
  isSystemAdmin: boolean;
  isProjectAdminAnywhere: boolean;
  /**
   * Settings ruling (2 Oct 2026): the Settings group (Workflows, SLA admin, Users, Projects) is for a SystemAdmin only. In
   * test mode the test identity decides, and no test role or viewed person is a SystemAdmin, so it is false whenever test
   * mode is on (the server refuses those endpoints then too).
   */
  canOpenSettings: boolean;
  rolesIn(projectKey: string): ProjectRole[];
  /**
   * Settings → Permissions (contract 0.10.36): may the caller do `code` on the project? A capability is always allowed to
   * a SystemAdmin (not while role test mode replaces the admin's roles on that project) — except a sidebar code (`menu.*`,
   * ruling of 17 Oct 2026: a SystemAdmin's sidebar comes from their project roles; the server never checks one); otherwise
   * the codes come from
   * `Me.projectPermissions` (the server's effective list), or the default matrix by role from an older server. Advisory:
   * the server still refuses.
   */
  can(projectKey: string, code: string): boolean;
  /** `Me.permissionsRevision`: the permission matrix revision the codes were read at (0 when never changed or not sent). */
  permissionsRevision: number;
  /**
   * Test mode (Features__RoleTestMode): present only when the signed-in person is a SystemAdmin who may use it. While
   * active, everything else in the session is the effective caller (the test role, or the viewed person).
   */
  testMode: Me["testMode"];
  /** A flag by name without the Features__ prefix. Unknown names are off (contract). */
  flag(name: string): boolean;
}

export const SessionContext = createContext<Session | null>(null);

export function useSession(): Session {
  const session = useContext(SessionContext);
  if (!session) throw new Error("useSession must be used inside SessionGate");
  return session;
}

/**
 * `Me.mustChangePassword` (auth ruling, SA Rev 29): while true only /auth/me, /features, change-password and logout are
 * allowed, so the change-password screen replaces every route.
 */
export function mustChangePassword(me: Me): boolean {
  return me.mustChangePassword;
}

export function buildSession(me: Me, flags: FeatureFlags["flags"]): Session {
  return {
    me,
    user: me.user,
    flags,
    modes: me.modes,
    locale: me.user.locale,
    isPartner: me.user.userKind === "partner",
    isSystemAdmin: me.globalRoles.includes("SystemAdmin"),
    isProjectAdminAnywhere: me.projectRoles.some((p) => p.roles.includes("ProjectAdmin")),
    canOpenSettings: me.user.userKind !== "partner" && me.globalRoles.includes("SystemAdmin") && !me.testMode?.active,
    rolesIn: (projectKey) => me.projectRoles.find((p) => p.projectKey === projectKey)?.roles ?? [],
    can: (projectKey, code) => (isCapability(code) && !isMenuCode(code) && systemAdminIn(me, projectKey)) || codesIn(me, projectKey).has(code),
    permissionsRevision: me.permissionsRevision ?? 0,
    testMode: me.testMode,
    flag: (name) => flags[name] === true,
  };
}

/** SystemAdmin powers on this project (same rule as `projectAccess.isSystemAdminIn`): never a partner, and not on the one
 * project where role test mode replaces the admin's roles. */
function systemAdminIn(me: Me, projectKey: string): boolean {
  const mode = me.testMode;
  return (
    me.user.userKind !== "partner" &&
    me.globalRoles.includes("SystemAdmin") &&
    !(mode?.active && mode.kind === "role" && mode.projectKey === projectKey)
  );
}
